import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Archive,
  Ban,
  BellRing,
  CheckCheck,
  Download,
  Eye,
  FileMinus,
  FilePlus,
  Mail,
  Pencil,
  RefreshCw,
  RotateCcw,
  Send,
  Trash2,
  Wallet,
} from "lucide-react";
import { useRateItems } from "@/api/clients";
import { useEmailDraft, useSendInvoiceEmail } from "@/api/emails";
import {
  documentFileName,
  fetchDocumentPdf,
  fetchPreviewPdf,
  useAmendInvoice,
  useCancelInvoice,
  useCloseInvoice,
  useCreateCreditNote,
  useDeleteInvoice,
  useInvoice,
  useInvoices,
  useIssueInvoice,
  useMarkSent,
  useRegenerateDocument,
  useReopenInvoice,
  useUpdateInvoice,
  type DocumentKind,
  type Invoice,
} from "@/api/invoices";
import {
  methodLabel,
  pickPaymentDetails,
  useRecordPayment,
  useUpdatePaymentDetails,
  useVoidPayment,
  type Payment,
} from "@/api/payments";
import { useMoneyAccounts, type MoneyAccount } from "@/api/cash";
import { fetchLatestReceiptPdf, useCollectReceipt, useIssueReceipt } from "@/api/receipts";
import { reminderLabel, useLateCharge, useSendReminder } from "@/api/receivables";
import { useBankAccounts, useCompanySettings } from "@/api/settings";
import { useVesselCalls } from "@/api/vessels";
import { BooksLink } from "@/components/books/BooksLink";
import { AmendInvoiceForm } from "@/components/forms/AmendInvoiceForm";
import { CreditNoteForm } from "@/components/forms/CreditNoteForm";
import { EmailForm } from "@/components/forms/EmailForm";
import { InvoiceDraftForm } from "@/components/forms/InvoiceDraftForm";
import { MarkSentForm, SENT_VIA_PHRASES } from "@/components/forms/MarkSentForm";
import { PaymentForm } from "@/components/forms/PaymentForm";
import { ReasonForm } from "@/components/forms/ReasonForm";
import { InvoiceStatusBadges, overdueLabel } from "@/components/invoices/InvoiceStatusBadges";
import { SplitCard } from "@/components/invoices/SplitCard";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button, buttonClass, type ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { fyCode, todayIso } from "@/lib/dates";
import { openPdf, saveBlob } from "@/lib/files";
import { formatDate, formatInr, formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";
import { billedCallIds } from "@/pages/NewInvoicePage";

type SimplePanel =
  | "edit"
  | "issue"
  | "delete"
  | "amend"
  | "regenerate"
  | "cancel"
  | "email"
  | "payment"
  | "mark-sent"
  | "close"
  | "reopen"
  | "remind"
  | "credit-note";

type Panel =
  | { kind: SimplePanel }
  | { kind: "payment-details" | "void-payment" | "receipt"; payment: Payment }
  | null;

const OPEN_STATUSES = new Set(["issued", "sent", "partially_paid"]);

const KIND_LABELS: Record<DocumentKind, string> = {
  original: "Original",
  regenerated: "Regenerated",
  amended: "Amended",
  imported: "Imported",
};

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm">{children}</dd>
    </div>
  );
}

function PanelCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="border-primary/40">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

type ConfirmProps = {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  variant?: ButtonVariant;
  pending: boolean;
  error: unknown;
  onConfirm: () => Promise<unknown>;
  onCancel: () => void;
  onReload: () => void;
};

function Confirm({
  title,
  children,
  confirmLabel,
  variant = "primary",
  pending,
  error,
  onConfirm,
  onCancel,
  onReload,
}: ConfirmProps) {
  return (
    <PanelCard title={title}>
      <div className="flex flex-col gap-4">
        <div className="text-sm">{children}</div>
        <RequestError error={error} onReload={onReload} />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Back
          </Button>
          <Button
            variant={variant}
            disabled={pending}
            onClick={() => void onConfirm().catch(() => undefined)}
          >
            {pending ? "Working…" : confirmLabel}
          </Button>
        </div>
      </div>
    </PanelCard>
  );
}

function DraftEditor({
  invoice,
  onDone,
  onReload,
}: {
  invoice: Invoice;
  onDone: () => void;
  onReload: () => void;
}) {
  const calls = useVesselCalls({ clientId: invoice.client_id, limit: 200 });
  const rateItems = useRateItems(invoice.client_id, false);
  const banks = useBankAccounts(false);
  const invoices = useInvoices({ clientId: invoice.client_id, limit: 200 });
  const update = useUpdateInvoice(invoice.id);

  const failed = [calls, rateItems, banks, invoices].find((query) => query.isError);
  if (failed) return <RequestError error={failed.error} />;
  if (!calls.data || !rateItems.data || !banks.data || !invoices.data) {
    return <p className="text-muted-foreground">Loading…</p>;
  }

  const billed = billedCallIds(invoices.data, invoice.id);
  return (
    <InvoiceDraftForm
      key={invoice.version}
      calls={calls.data.filter((call) =>
        // An additional invoice stays on the call of the invoice it is additional to.
        invoice.additional_to_id ? call.id === invoice.vessel_call_id : !billed.has(call.id),
      )}
      rateItems={rateItems.data}
      bankAccounts={banks.data}
      currency={invoice.currency}
      initial={{
        vessel_call_id: invoice.vessel_call_id ?? "",
        invoice_date: invoice.invoice_date,
        payment_days: invoice.payment_days,
        bank_account_id: invoice.bank_account_id,
        remarks: invoice.remarks,
        // Typed lines go back in too, or saving the draft would drop them.
        lines: invoice.lines.map((line) =>
          line.rate_item_id
            ? {
                rate_item_id: line.rate_item_id,
                qty: line.qty,
                description: line.description,
                detail: line.detail,
              }
            : {
                qty: line.qty,
                description: line.description,
                detail: line.detail,
                unit_rate: line.unit_rate,
                rate_group: line.rate_group === "A" ? ("A" as const) : ("B" as const),
                item_no: line.item_no,
              },
        ),
        discount:
          Number(invoice.discount_amount) > 0
            ? {
                description: invoice.discount_description,
                amount: invoice.discount_amount,
                note: invoice.discount_note,
              }
            : null,
        extra_notes: invoice.extra_notes,
      }}
      submitLabel="Save draft"
      onSubmit={async (input) => {
        await update.mutateAsync({ input, version: invoice.version });
        onDone();
      }}
      onCancel={onDone}
      onReload={onReload}
    />
  );
}

function EmailPanel({
  invoice,
  onDone,
  onCancel,
  onReload,
}: {
  invoice: Invoice;
  onDone: (message: string) => void;
  onCancel: () => void;
  onReload: () => void;
}) {
  const draft = useEmailDraft(invoice.id);
  const company = useCompanySettings();
  const send = useSendInvoiceEmail(invoice.id);
  const markSent = useMarkSent(invoice.id);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<unknown>(null);

  if (draft.isError || company.isError) {
    return <RequestError error={draft.error ?? company.error} />;
  }
  if (!draft.data || !company.data) {
    return <p className="text-muted-foreground">Preparing the e-mail…</p>;
  }
  const version = draft.data.document_version;
  const mailbox = company.data.email;

  function download(): void {
    if (version === null) return;
    setDownloading(true);
    setDownloadError(null);
    fetchDocumentPdf(invoice.id, version, documentFileName(invoice, version))
      .then((file) => saveBlob(file.blob, file.filename))
      .catch((error: unknown) => setDownloadError(error))
      .finally(() => setDownloading(false));
  }

  return (
    <div className="flex flex-col gap-3">
      <RequestError error={downloadError} />
      <EmailForm
        draft={draft.data}
        mailbox={mailbox}
        alreadySent={invoice.sent_on !== null}
        downloading={downloading}
        onDownload={download}
        onSend={async (input) => {
          await send.mutateAsync(input);
          onDone(`E-mailed ${invoice.invoice_no} to ${input.to.join(", ")}.`);
        }}
        onMarkEmailed={async (to) => {
          const recipients = to.length > 0 ? ` to ${to.join(", ")}` : "";
          const note = `e-mailed from ${mailbox}${recipients}`.slice(0, 200);
          await markSent.mutateAsync({
            input: { sent_on: todayIso(), via: "email", note },
            version: invoice.version,
          });
          onDone(`Recorded as e-mailed from ${mailbox}.`);
        }}
        onCancel={onCancel}
        onReload={onReload}
      />
    </div>
  );
}

function LinesCard({ invoice }: { invoice: Invoice }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Items</CardTitle>
      </CardHeader>
      <CardContent>
        {invoice.lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">No items yet - edit the draft to add them.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">No.</th>
                  <th className="py-2 pr-3 font-medium">Description</th>
                  <th className="py-2 pr-3 text-right font-medium">Qty</th>
                  <th className="hidden py-2 pr-3 text-right font-medium sm:table-cell">Rate</th>
                  <th className="py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((line) => (
                  <tr key={line.line_no} className="border-b align-top last:border-0">
                    <td className="py-2 pr-3">
                      {line.item_no}
                      <span className="block text-xs text-muted-foreground">{line.rate_group}</span>
                    </td>
                    <td className="py-2 pr-3">
                      {line.description}
                      {line.detail && (
                        <span className="block text-xs text-muted-foreground">{line.detail}</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{Number(line.qty)}</td>
                    <td className="hidden whitespace-nowrap py-2 pr-3 text-right tabular-nums sm:table-cell">
                      {formatMoney(line.unit_rate, invoice.currency)}
                    </td>
                    <td className="whitespace-nowrap py-2 text-right tabular-nums">
                      {formatMoney(line.amount, invoice.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {Number(invoice.discount_amount) > 0 && (
                  <>
                    <tr>
                      <td colSpan={2} className="pt-3">
                        Subtotal
                      </td>
                      <td colSpan={3} className="whitespace-nowrap pt-3 text-right tabular-nums">
                        {formatMoney(invoice.subtotal, invoice.currency)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="pt-1">
                        Less discount
                        <span className="block text-xs text-muted-foreground">
                          {invoice.discount_description}
                        </span>
                      </td>
                      <td colSpan={3} className="whitespace-nowrap pt-1 text-right tabular-nums">
                        ({formatMoney(invoice.discount_amount, invoice.currency)})
                      </td>
                    </tr>
                  </>
                )}
                <tr>
                  <td colSpan={2} className="pt-3 font-semibold">
                    Total
                  </td>
                  <td
                    colSpan={3}
                    className="whitespace-nowrap pt-3 text-right text-base font-semibold tabular-nums"
                  >
                    {formatMoney(invoice.total, invoice.currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReceiptLink({ payment }: { payment: Payment }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  function download(): void {
    if (!payment.receipt_id) return;
    setBusy(true);
    setError(null);
    fetchLatestReceiptPdf(payment.receipt_id)
      .then((file) => saveBlob(file.blob, file.filename))
      .catch((err: unknown) => setError(err))
      .finally(() => setBusy(false));
  }

  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge variant="secondary">{payment.receipt_no}</Badge>
      <Button size="sm" variant="outline" onClick={download} disabled={busy}>
        <Download className="h-3.5 w-3.5" aria-hidden />
        {busy ? "Preparing…" : "Receipt"}
      </Button>
      {error !== null && <RequestError error={error} />}
    </span>
  );
}

/** "Cash (SAR 5,250.00 at 3.75)" - what a director needs to recognise the payment. */
function arrivalLine(payment: Payment): string {
  const how = methodLabel(payment.method);
  const foreign =
    payment.received_currency && payment.received_amount
      ? ` (${formatMoney(payment.received_amount, payment.received_currency)}${
          payment.received_rate ? ` at ${Number(payment.received_rate)}` : ""
        })`
      : "";
  const who = payment.received_by ? ` · received by ${payment.received_by}` : "";
  const where = payment.received_place ? ` at ${payment.received_place}` : "";
  return `${how}${foreign}${who}${where}`;
}

function PaymentsCard({
  invoice,
  onDetails,
  onVoid,
  onReceipt,
}: {
  invoice: Invoice;
  onDetails: (payment: Payment) => void;
  onVoid: (payment: Payment) => void;
  onReceipt: (payment: Payment) => void;
}) {
  const currency = invoice.currency;
  const editable = invoice.state === "issued";
  return (
    <Card>
      <CardHeader>
        <CardTitle>Payments</CardTitle>
        <CardDescription>
          Paid {formatMoney(invoice.paid_amount, currency)} · balance{" "}
          {formatMoney(invoice.balance, currency)}
          {Number(invoice.write_off_amount) > 0 &&
            ` · written off ${formatMoney(invoice.write_off_amount, currency)}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {invoice.payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
        ) : (
          <ul className="flex flex-col divide-y">
            {invoice.payments.map((payment) => {
              const voided = payment.voided_at !== null;
              return (
                <li
                  key={payment.id}
                  className={cn(
                    "flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between",
                    voided && "opacity-70",
                  )}
                >
                  <div className="min-w-0 text-sm">
                    <p className="flex flex-wrap items-center gap-2">
                      <span className={cn("font-semibold", voided && "line-through")}>
                        {formatMoney(payment.amount, currency)}
                      </span>
                      <span className="text-muted-foreground">received {formatDate(payment.received_on)}</span>
                      {voided && <Badge variant="destructive">Voided</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">{arrivalLine(payment)}</p>
                    <p className="text-xs text-muted-foreground">
                      {Number(payment.bank_charges) > 0
                        ? `Bank charges ${formatMoney(payment.bank_charges, currency)} · net ${formatMoney(payment.net_amount, currency)}`
                        : "No bank charges"}
                      {payment.reference ? ` · ref ${payment.reference}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {payment.inr_credited ? formatInr(payment.inr_credited) : "INR not recorded"}
                      {payment.fx_rate ? ` at ${Number(payment.fx_rate)}` : ""}
                      {payment.fira_ref ? ` · eFIRA ${payment.fira_ref}` : " · eFIRA pending"}
                      {payment.purpose_code ? ` · ${payment.purpose_code}` : ""}
                    </p>
                    {payment.notes && <p className="text-xs">{payment.notes}</p>}
                    {voided && (
                      <p className="text-xs text-destructive">
                        Voided {formatDate(payment.voided_at)}
                        {payment.voided_by_username ? ` by ${payment.voided_by_username}` : ""}:{" "}
                        {payment.void_reason}
                      </p>
                    )}
                  </div>
                  {editable && !voided && (
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      {payment.receipt_no ? (
                        <ReceiptLink payment={payment} />
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => onReceipt(payment)}>
                          Issue receipt
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => onDetails(payment)}>
                        Bank details
                      </Button>
                      {invoice.closed_at === null && (
                        <Button size="sm" variant="ghost" onClick={() => onVoid(payment)}>
                          Void
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Record a payment; ticking the client's other open invoices records one collection for
 *  them all, acknowledged on one receipt (PBO/R/26-27/0102: SAR for 0102 and 0103). */
function RecordPaymentPanel({
  invoice,
  accounts,
  onCancel,
  onReload,
  onDone,
}: {
  invoice: Invoice;
  accounts: MoneyAccount[] | undefined;
  onCancel: () => void;
  onReload: () => void;
  onDone: (message: string) => void;
}) {
  const recordPayment = useRecordPayment(invoice.id);
  const collectReceipt = useCollectReceipt();
  const unpaid = useInvoices({ clientId: invoice.client_id, status: "unpaid", limit: 200 });
  const others = (unpaid.data ?? []).filter(
    (row) =>
      row.id !== invoice.id &&
      row.doc_type === "invoice" &&
      row.state === "issued" &&
      row.closed_at === null &&
      row.currency === invoice.currency &&
      Number(row.balance) > 0,
  );
  return (
    <PanelCard title="Record a payment">
      <PaymentForm
        mode="record"
        currency={invoice.currency}
        balance={invoice.balance}
        accounts={accounts}
        submitLabel="Record payment"
        onCancel={onCancel}
        onReload={onReload}
        onSubmit={async (input) => {
          await recordPayment.mutateAsync(input);
          onDone(`Payment of ${formatMoney(input.amount, invoice.currency)} recorded.`);
        }}
        collect={{
          invoiceId: invoice.id,
          others,
          onSubmit: async (input) => {
            const receipt = await collectReceipt.mutateAsync(input);
            onDone(
              `Payments recorded on ${input.lines.length} invoices and receipt ${receipt.receipt_no} issued.`,
            );
          },
        }}
      />
    </PanelCard>
  );
}

const EMAIL_STATUS = {
  sent: { label: "Sent", variant: "success" },
  failed: { label: "Failed", variant: "destructive" },
  sending: { label: "Outcome unknown", variant: "muted" },
} as const;

function EmailsCard({ invoice }: { invoice: Invoice }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>E-mails</CardTitle>
        <CardDescription>
          {invoice.sent_on
            ? `Sent to the client ${formatDate(invoice.sent_on)}${
                invoice.sent_via && SENT_VIA_PHRASES[invoice.sent_via]
                  ? ` ${SENT_VIA_PHRASES[invoice.sent_via]}`
                  : ""
              }${invoice.sent_note ? ` - ${invoice.sent_note}` : ""}`
            : "Not recorded as sent to the client yet."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {invoice.emails.length === 0 ? (
          <p className="text-sm text-muted-foreground">No e-mails from the app yet.</p>
        ) : (
          <ul className="flex flex-col divide-y">
            {invoice.emails.map((email) => (
              <li key={email.id} className="flex flex-col gap-1 py-3 text-sm first:pt-0 last:pb-0">
                <p className="flex flex-wrap items-center gap-2">
                  <Badge variant={EMAIL_STATUS[email.status].variant}>
                    {EMAIL_STATUS[email.status].label}
                  </Badge>
                  {reminderLabel(email) && (
                    <Badge variant="secondary">{reminderLabel(email)}</Badge>
                  )}
                  <span className="font-medium">{email.subject}</span>
                </p>
                <p className="break-words text-xs text-muted-foreground">
                  To {email.to_addrs.join(", ")}
                  {email.cc_addrs.length > 0 ? ` · cc ${email.cc_addrs.join(", ")}` : ""} ·{" "}
                  {formatDate(email.created_at)}
                  {email.created_by_username
                    ? ` by ${email.created_by_username}`
                    : email.reminder_kind
                      ? " by the daily reminder job"
                      : ""} · PDF
                  version {email.document_version}
                </p>
                {email.error && <p className="text-xs text-destructive">{email.error}</p>}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** What the balance has accrued under the late-payment clause, and the way to bill it: on the
 *  client's next invoice, as a line of its own. Nothing is billed until a director saves it. */
function LateChargeCard({ invoice }: { invoice: Invoice }) {
  const charge = useLateCharge(invoice.id);
  if (!charge.data || Number(charge.data.amount) <= 0) return null;
  const data = charge.data;
  const left = Number(data.unbilled) > 0;
  const billedNote =
    Number(data.billed) > 0
      ? `; ${formatMoney(data.billed, data.currency)} already billed on ${data.billed_on.join(", ")}`
      : "";
  return (
    <Card>
      <CardHeader>
        <CardTitle>Late charge</CardTitle>
        <CardDescription>
          Accrued under SLA 6.5{billedNote}.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm">
          <strong className="text-lg">
            {left ? formatMoney(data.unbilled, data.currency) : "Nothing"}
          </strong>{" "}
          <span className="text-muted-foreground">
            {left ? `not yet billed - ${data.detail}` : "left to bill"}
          </span>
        </p>
        {left && (
          <Link
            to={`/invoices/new?client=${invoice.client_id}&late_charge=${invoice.id}`}
            className={buttonClass("outline", "sm")}
          >
            Bill late charge
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

function CreditNotesCard({ invoice }: { invoice: Invoice }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Credit notes</CardTitle>
        <CardDescription>
          Credited {formatMoney(invoice.credited, invoice.currency)}; drafts and cancelled credit
          notes do not count.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col divide-y text-sm">
          {invoice.credit_notes.map((note) => (
            <li
              key={note.id}
              className="flex flex-wrap items-center gap-2 py-2 first:pt-0 last:pb-0"
            >
              <Link
                to={`/invoices/${note.id}`}
                className="font-medium text-primary hover:underline"
              >
                {note.invoice_no ?? "Draft"}
              </Link>
              <Badge variant={note.state === "cancelled" ? "destructive" : "secondary"}>
                {note.state}
              </Badge>
              <span className="text-muted-foreground">{formatDate(note.invoice_date)}</span>
              <span className="ml-auto tabular-nums">{formatMoney(note.total, note.currency)}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function InvoiceDetailPage() {
  const { invoiceId = "" } = useParams();
  const navigate = useNavigate();
  const invoice = useInvoice(invoiceId);
  const banks = useBankAccounts(true);
  const issue = useIssueInvoice(invoiceId);
  const amend = useAmendInvoice(invoiceId);
  const regenerate = useRegenerateDocument(invoiceId);
  const cancel = useCancelInvoice(invoiceId);
  const remove = useDeleteInvoice(invoiceId);
  const markSent = useMarkSent(invoiceId);
  const close = useCloseInvoice(invoiceId);
  const reopen = useReopenInvoice(invoiceId);
  const remind = useSendReminder(invoiceId);
  const createCredit = useCreateCreditNote(invoiceId);
  const moneyAccounts = useMoneyAccounts(true);
  const paymentDetails = useUpdatePaymentDetails(invoiceId);
  const voidPayment = useVoidPayment(invoiceId);
  const issueReceipt = useIssueReceipt();
  const [panel, setPanel] = useState<Panel>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fileError, setFileError] = useState<unknown>(null);
  const [fileBusy, setFileBusy] = useState(false);

  if (invoice.isPending) {
    return <p className="text-muted-foreground">Loading invoice…</p>;
  }
  if (invoice.isError) {
    return <RequestError error={invoice.error} />;
  }

  const data = invoice.data;
  const currency = data.currency;
  const printed = data.snapshot;
  const currentVersion = data.current_document_version;
  const isOpen = OPEN_STATUSES.has(data.status);
  const isCredit = data.doc_type === "credit_note";
  const reload = () => {
    setPanel(null);
    void invoice.refetch();
  };

  function open(next: Panel): void {
    for (const mutation of [issue, remove]) mutation.reset();
    setNotice(null);
    setPanel(next);
  }

  function done(message: string | null): void {
    setPanel(null);
    setNotice(message);
  }

  async function withFile(action: () => Promise<void>): Promise<void> {
    setFileError(null);
    setFileBusy(true);
    try {
      await action();
    } catch (error) {
      setFileError(error);
    } finally {
      setFileBusy(false);
    }
  }

  const download = (version: number) =>
    withFile(async () => {
      const file = await fetchDocumentPdf(data.id, version, documentFileName(data, version));
      await saveBlob(file.blob, file.filename);
    });

  const bankText = printed
    ? printed.bank
      ? `${printed.bank.bank} · A/c ${printed.bank.account_no}`
      : "To follow on letterhead (SLA 6.4)"
    : data.bank_account_id
      ? (banks.data?.find((bank) => bank.id === data.bank_account_id)?.label ?? "Chosen account")
      : "None yet - details follow on letterhead";

  const dueText = data.effective_due_date
    ? formatDate(data.effective_due_date)
    : data.awaiting_bank_letter
      ? `${data.payment_days} days after the bank letter`
      : formatDate(data.due_date);

  const actions =
    data.state === "draft" ? (
      <>
        <Button
          variant="outline"
          size="sm"
          disabled={fileBusy}
          onClick={() =>
            void withFile(() => openPdf(() => fetchPreviewPdf(data.id), "draft-invoice.pdf"))
          }
        >
          <Eye className="h-4 w-4" aria-hidden />
          Preview PDF
        </Button>
        {!isCredit && (
          <Button variant="outline" size="sm" onClick={() => open({ kind: "edit" })}>
            <Pencil className="h-4 w-4" aria-hidden />
            Edit
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => open({ kind: "delete" })}>
          <Trash2 className="h-4 w-4" aria-hidden />
          Delete
        </Button>
        <Button size="sm" onClick={() => open({ kind: "issue" })}>
          <Send className="h-4 w-4" aria-hidden />
          Issue invoice
        </Button>
      </>
    ) : (
      <>
        {currentVersion !== null && (
          <Button
            size="sm"
            variant={data.state === "issued" ? "outline" : "primary"}
            disabled={fileBusy}
            onClick={() => void download(currentVersion)}
          >
            <Download className="h-4 w-4" aria-hidden />
            Download PDF
          </Button>
        )}
        {data.state === "issued" && (
          <>
            <Button size="sm" variant="outline" onClick={() => open({ kind: "email" })}>
              <Mail className="h-4 w-4" aria-hidden />
              E-mail
            </Button>
            {!isCredit && (
              <Button size="sm" variant="outline" onClick={() => open({ kind: "credit-note" })}>
                <FileMinus className="h-4 w-4" aria-hidden />
                Credit note
              </Button>
            )}
            {!isCredit && isOpen && !data.awaiting_bank_letter && (
              <Button size="sm" variant="outline" onClick={() => open({ kind: "remind" })}>
                <BellRing className="h-4 w-4" aria-hidden />
                Send reminder
              </Button>
            )}
            {!isCredit && isOpen && (
              <Button size="sm" onClick={() => open({ kind: "payment" })}>
                <Wallet className="h-4 w-4" aria-hidden />
                Record payment
              </Button>
            )}
          </>
        )}
      </>
    );

  const moreActions = data.state === "issued" && (
    <div className="flex flex-wrap gap-2">
      {data.sent_on === null && (
        <Button size="sm" variant="ghost" onClick={() => open({ kind: "mark-sent" })}>
          <CheckCheck className="h-4 w-4" aria-hidden />
          Mark as sent
        </Button>
      )}
      {!isCredit && !data.additional_to_id && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate(`/invoices/new?client=${data.client_id}&additional_to=${data.id}`)}
        >
          <FilePlus className="h-4 w-4" aria-hidden />
          Additional invoice
        </Button>
      )}
      {!isCredit && (
        <Button size="sm" variant="ghost" onClick={() => open({ kind: "amend" })}>
          <Pencil className="h-4 w-4" aria-hidden />
          Amend details
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => open({ kind: "regenerate" })}>
        <RefreshCw className="h-4 w-4" aria-hidden />
        Regenerate PDF
      </Button>
      {!isCredit && isOpen && (
        <Button size="sm" variant="ghost" onClick={() => open({ kind: "close" })}>
          <Archive className="h-4 w-4" aria-hidden />
          Close with write-off
        </Button>
      )}
      {data.status === "closed" && (
        <Button size="sm" variant="ghost" onClick={() => open({ kind: "reopen" })}>
          <RotateCcw className="h-4 w-4" aria-hidden />
          Reopen
        </Button>
      )}
      {Number(data.paid_amount) === 0 && data.closed_at === null && Number(data.credited) === 0 && (
        <Button size="sm" variant="ghost" onClick={() => open({ kind: "cancel" })}>
          <Ban className="h-4 w-4" aria-hidden />
          {isCredit ? "Cancel credit note" : "Cancel invoice"}
        </Button>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <Link to="/invoices" className="text-sm text-muted-foreground hover:text-foreground">
        ← Invoices
      </Link>
      <PageHeader
        title={data.invoice_no ?? (isCredit ? "Draft credit note" : "Draft invoice")}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {isCredit && <Badge variant="secondary">Credit note</Badge>}
            <InvoiceStatusBadges invoice={data} />
            {isCredit && data.original_invoice_id && (
              <span className="text-sm">
                against{" "}
                <Link
                  to={`/invoices/${data.original_invoice_id}`}
                  className="text-primary hover:underline"
                >
                  {data.original_invoice_no}
                </Link>
              </span>
            )}
            {data.additional_to_id && (
              <span className="text-sm">
                additional to{" "}
                <Link
                  to={`/invoices/${data.additional_to_id}`}
                  className="text-primary hover:underline"
                >
                  {data.additional_to_no}
                </Link>
              </span>
            )}
            <Badge>{data.client_code}</Badge>
            {data.source === "legacy_import" && (
              <Badge variant="muted">Imported from the legacy register</Badge>
            )}
            {data.state === "issued" && (
              <BooksLink kind={isCredit ? "credit_note" : "invoice"} id={data.id} />
            )}
          </span>
        }
        actions={actions}
      />
      {moreActions}

      {notice && <Alert variant="success">{notice}</Alert>}
      <RequestError error={fileError} />
      {data.state === "draft" && panel === null && (
        <Alert title="Draft - no number yet">
          Check the PDF preview, then issue the invoice to give it the next number.
        </Alert>
      )}
      {data.is_overdue && (
        <Alert
          variant="error"
          title={`${overdueLabel(data.days_overdue)} - ${formatMoney(data.balance, currency)} outstanding`}
        >
          It was due on {formatDate(data.effective_due_date)}.
        </Alert>
      )}
      {data.awaiting_bank_letter && (
        <Alert title="Waiting for the bank details letter">
          Issued without bank details, so payment falls due {data.payment_days} days after the
          client receives the Director-signed bank letter. When the letter goes out, record its
          date on the bank account in Settings.
        </Alert>
      )}
      {data.status === "paid" && <Alert variant="success">Paid in full.</Alert>}
      {data.status === "closed" && (
        <Alert
          title={`Closed ${formatDate(data.closed_at)}${
            data.closed_by_username ? ` by ${data.closed_by_username}` : ""
          } - ${formatMoney(data.write_off_amount, currency)} written off`}
        >
          {data.close_reason}
        </Alert>
      )}
      {data.state === "cancelled" && (
        <Alert
          variant="warning"
          title={`Cancelled ${formatDate(data.cancelled_at)}${
            data.cancelled_by_username ? ` by ${data.cancelled_by_username}` : ""
          }`}
        >
          {data.cancel_reason} The number stays used in the register.
        </Alert>
      )}

      {panel?.kind === "edit" && data.state === "draft" && (
        <PanelCard title="Edit draft">
          <DraftEditor invoice={data} onDone={() => setPanel(null)} onReload={reload} />
        </PanelCard>
      )}
      {panel?.kind === "issue" && (
        <Confirm
          title="Issue this invoice?"
          confirmLabel="Issue invoice"
          pending={issue.isPending}
          error={issue.error}
          onCancel={() => setPanel(null)}
          onReload={reload}
          onConfirm={async () => {
            const issued = await issue.mutateAsync(data.version);
            done(`Issued as ${issued.invoice_no}. The PDF is stored and ready to download.`);
          }}
        >
          <p>
            It takes the next number in the FY {fyCode(data.invoice_date)} series and its PDF is
            stored permanently. The number can never be reused or deleted: afterwards only the
            vessel-call details and the notes can be amended, or the invoice cancelled.
          </p>
          <p className="mt-2 font-medium">
            {formatMoney(data.total, currency)} to {data.client_name}, dated{" "}
            {formatDate(data.invoice_date)}.
          </p>
        </Confirm>
      )}
      {panel?.kind === "delete" && (
        <Confirm
          title="Delete this draft?"
          confirmLabel="Delete draft"
          variant="destructive"
          pending={remove.isPending}
          error={remove.error}
          onCancel={() => setPanel(null)}
          onReload={reload}
          onConfirm={async () => {
            await remove.mutateAsync(data.version);
            navigate("/invoices");
          }}
        >
          The draft has no number, so nothing is lost from the register. Its vessel call can be
          invoiced again.
        </Confirm>
      )}
      {panel?.kind === "email" && (
        <PanelCard title={`E-mail ${data.invoice_no}`}>
          <EmailPanel
            invoice={data}
            onDone={(message) => done(message)}
            onCancel={() => setPanel(null)}
            onReload={reload}
          />
        </PanelCard>
      )}
      {panel?.kind === "credit-note" && (
        <PanelCard title={`Credit note against ${data.invoice_no}`}>
          <CreditNoteForm
            currency={currency}
            maxAmount={(Number(data.total) - Number(data.credited)).toFixed(2)}
            onCancel={() => setPanel(null)}
            onReload={reload}
            onSubmit={async (input) => {
              const note = await createCredit.mutateAsync(input);
              navigate(`/invoices/${note.id}`);
            }}
          />
        </PanelCard>
      )}
      {panel?.kind === "remind" && (
        <Confirm
          title={`Send a payment reminder for ${data.invoice_no}`}
          confirmLabel="Send reminder"
          pending={remind.isPending}
          error={remind.error}
          onConfirm={async () => {
            await remind.mutateAsync();
            done("Reminder sent.");
          }}
          onCancel={() => setPanel(null)}
          onReload={reload}
        >
          It goes to the client's contacts who receive invoices, with the invoice attached, and
          says the balance of {formatMoney(data.balance, currency)}{" "}
          {data.is_overdue
            ? `is ${data.days_overdue} day${data.days_overdue === 1 ? "" : "s"} overdue`
            : `falls due on ${dueText}`}
          .
        </Confirm>
      )}
      {panel?.kind === "payment" && (
        <RecordPaymentPanel
          invoice={data}
          accounts={moneyAccounts.data}
          onCancel={() => setPanel(null)}
          onReload={reload}
          onDone={done}
        />
      )}
      {panel?.kind === "payment-details" && (
        <PanelCard title={`Bank details of the ${formatMoney(panel.payment.amount, currency)} payment`}>
          <PaymentForm
            key={panel.payment.version}
            mode="details"
            currency={currency}
            balance={data.balance}
            initial={panel.payment}
            accounts={moneyAccounts.data}
            submitLabel="Save bank details"
            onCancel={() => setPanel(null)}
            onReload={reload}
            onSubmit={async (input) => {
              await paymentDetails.mutateAsync({
                id: panel.payment.id,
                input: pickPaymentDetails(input),
                version: panel.payment.version,
              });
              done("Bank details saved.");
            }}
          />
        </PanelCard>
      )}
      {panel?.kind === "void-payment" && (
        <PanelCard title={`Void the ${formatMoney(panel.payment.amount, currency)} payment?`}>
          <ReasonForm
            label="Reason for voiding"
            hint="e.g. recorded twice, or the transfer was returned"
            submitLabel="Void payment"
            submitVariant="destructive"
            onCancel={() => setPanel(null)}
            onReload={reload}
            onSubmit={async (reason) => {
              await voidPayment.mutateAsync({
                id: panel.payment.id,
                reason,
                version: panel.payment.version,
              });
              done("Payment voided. It stays on record, struck through.");
            }}
          >
            <p className="text-sm text-muted-foreground">
              A voided payment no longer counts towards the invoice, but it is never deleted.
            </p>
          </ReasonForm>
        </PanelCard>
      )}
      {panel?.kind === "receipt" && (
        <Confirm
          title={`Issue a receipt for ${formatMoney(panel.payment.amount, currency)}?`}
          confirmLabel="Issue receipt"
          pending={issueReceipt.isPending}
          error={issueReceipt.error}
          onCancel={() => setPanel(null)}
          onReload={reload}
          onConfirm={async () => {
            const receipt = await issueReceipt.mutateAsync({
              lines: [{ payment_id: panel.payment.id }],
            });
            done(`Receipt ${receipt.receipt_no} issued.`);
          }}
        >
          <p>
            The receipt takes the next number in the receipt series and cannot be edited
            afterwards - a mistake is cancelled and a new one issued.
          </p>
          <p className="mt-2 text-muted-foreground">
            It acknowledges {arrivalLine(panel.payment)}, received{" "}
            {formatDate(panel.payment.received_on)}.
          </p>
        </Confirm>
      )}
      {panel?.kind === "mark-sent" && (
        <PanelCard title="Mark as sent">
          <MarkSentForm
            onCancel={() => setPanel(null)}
            onReload={reload}
            onSubmit={async (input) => {
              await markSent.mutateAsync({ input, version: data.version });
              done("Recorded as sent.");
            }}
          />
        </PanelCard>
      )}
      {panel?.kind === "amend" && printed && (
        <PanelCard title="Amend details and notes">
          <AmendInvoiceForm
            initial={printed.vessel_call}
            discount={printed.discount ?? null}
            extraNotes={printed.extra_notes ?? []}
            onCancel={() => setPanel(null)}
            onReload={reload}
            onSubmit={async (input) => {
              const amended = await amend.mutateAsync({ input, version: data.version });
              done(`Amended. PDF version ${amended.current_document_version} is stored.`);
            }}
          />
        </PanelCard>
      )}
      {panel?.kind === "regenerate" && (
        <PanelCard title="Regenerate the PDF">
          <ReasonForm
            label="Reason"
            hint="e.g. layout correction after an app update"
            submitLabel="Store new PDF version"
            onCancel={() => setPanel(null)}
            onReload={reload}
            onSubmit={async (reason) => {
              const updated = await regenerate.mutateAsync({ reason, version: data.version });
              done(`PDF version ${updated.current_document_version} is stored.`);
            }}
          >
            <p className="text-sm text-muted-foreground">
              Renders the invoice again from exactly what was issued. Earlier versions are kept.
            </p>
          </ReasonForm>
        </PanelCard>
      )}
      {panel?.kind === "close" && (
        <PanelCard title="Close with a write-off?">
          <ReasonForm
            label="Reason"
            hint="e.g. intermediary bank charges the client did not cover"
            submitLabel={`Write off ${formatMoney(data.balance, currency)}`}
            submitVariant="destructive"
            onCancel={() => setPanel(null)}
            onReload={reload}
            onSubmit={async (reason) => {
              await close.mutateAsync({ reason, version: data.version });
              done("Invoice closed.");
            }}
          >
            <Alert variant="warning">
              The remaining {formatMoney(data.balance, currency)} is written off and{" "}
              {data.invoice_no} stops counting as outstanding. Reopen it if the money arrives
              later.
            </Alert>
          </ReasonForm>
        </PanelCard>
      )}
      {panel?.kind === "reopen" && (
        <PanelCard title="Reopen this invoice?">
          <ReasonForm
            label="Reason"
            submitLabel="Reopen invoice"
            onCancel={() => setPanel(null)}
            onReload={reload}
            onSubmit={async (reason) => {
              await reopen.mutateAsync({ reason, version: data.version });
              done("Invoice reopened.");
            }}
          >
            <p className="text-sm text-muted-foreground">
              The written-off {formatMoney(data.write_off_amount, currency)} is owed again.
            </p>
          </ReasonForm>
        </PanelCard>
      )}
      {panel?.kind === "cancel" && (
        <PanelCard title="Cancel this invoice?">
          <ReasonForm
            label="Reason for cancelling"
            hint="Kept in the audit log and shown on the invoice record"
            submitLabel="Cancel invoice"
            submitVariant="destructive"
            onCancel={() => setPanel(null)}
            onReload={reload}
            onSubmit={async (reason) => {
              await cancel.mutateAsync({ reason, version: data.version });
              done("The invoice is cancelled. Its vessel call can be invoiced again.");
            }}
          >
            <Alert variant="warning">
              Cancelling cannot be undone. {data.invoice_no} stays in the register as cancelled,
              so the numbering has no gap. Tell the client if the invoice was already sent.
            </Alert>
          </ReasonForm>
        </PanelCard>
      )}

      <Card>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Detail label="Client">{data.client_name}</Detail>
            <Detail label="Vessel">
              {data.vessel_name ?? "—"}
              {data.voyage ? ` · voyage ${data.voyage}` : ""}
            </Detail>
            <Detail label="Departure">{formatDate(data.departure)}</Detail>
            {printed && <Detail label="Terminal">{printed.vessel_call.terminal}</Detail>}
            <Detail label="Invoice date">{formatDate(data.invoice_date)}</Detail>
            {!isCredit && <Detail label="Due">{dueText}</Detail>}
            {!isCredit && <Detail label="Bank details">{bankText}</Detail>}
            <Detail label="Total">
              <span className="text-base font-semibold">{formatMoney(data.total, currency)}</span>
            </Detail>
            {data.state !== "draft" && !isCredit && (
              <>
                <Detail label="Paid">{formatMoney(data.paid_amount, currency)}</Detail>
                <Detail label="Balance">
                  <span className={cn("font-semibold", data.is_overdue && "text-destructive")}>
                    {formatMoney(data.balance, currency)}
                  </span>
                </Detail>
              </>
            )}
            {data.issued_at && (
              <Detail label="Issued">
                {formatDate(data.issued_at)}
                {data.issued_by_username ? ` by ${data.issued_by_username}` : ""}
              </Detail>
            )}
          </dl>
          {data.remarks && (
            <p className="mt-4 text-sm text-muted-foreground">Remarks: {data.remarks}</p>
          )}
        </CardContent>
      </Card>

      <LinesCard invoice={data} />

      {data.state !== "draft" && (
        <>
          {!isCredit && (
            <PaymentsCard
              invoice={data}
              onDetails={(payment) => open({ kind: "payment-details", payment })}
              onVoid={(payment) => open({ kind: "void-payment", payment })}
              onReceipt={(payment) => open({ kind: "receipt", payment })}
            />
          )}
          {!isCredit && data.credit_notes.length > 0 && <CreditNotesCard invoice={data} />}
          {!isCredit && data.state === "issued" && <LateChargeCard invoice={data} />}
          {!isCredit && data.state === "issued" && <SplitCard invoiceId={data.id} />}
          <EmailsCard invoice={data} />
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Stored PDFs</CardTitle>
          <CardDescription>Every version is kept exactly as it was produced.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.documents.length === 0 ? (
            <p className="text-sm text-muted-foreground">The PDF is stored when the invoice is issued.</p>
          ) : (
            <ul className="flex flex-col divide-y">
              {[...data.documents].reverse().map((doc) => (
                <li
                  key={doc.version}
                  className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      Version {doc.version}
                      <Badge variant="secondary">{KIND_LABELS[doc.kind]}</Badge>
                      {doc.version === currentVersion && <Badge variant="success">Current</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(doc.created_at)}
                      {doc.created_by_username ? ` by ${doc.created_by_username}` : ""}
                      {doc.reason ? ` - ${doc.reason}` : ""}
                    </p>
                    <p className="break-all font-mono text-[11px] text-muted-foreground">
                      SHA-256 {doc.sha256}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={fileBusy}
                    onClick={() => void download(doc.version)}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    Download
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
